import React from 'react';
import { Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';

import QuoteStore from '../../stores/QuoteStore';
import { summarizeQuoteRequirements } from '../../utils/quoteRequirements';

interface Props {
    onEdit: () => void;
}

/** Quote-level requirements at a glance on Parts & Specifications; editing happens in the Requirements modal. */
export const QuoteRequirementsSummary = observer(({ onEdit }: Props) => {
    const { total, items, hiddenCount, missingDefaultsCount } = summarizeQuoteRequirements(
        QuoteStore.quote?.custom_requirements ?? [],
        QuoteStore.default_requirements,
        QuoteStore.additional_requirements
    );

    return (
        <section
            aria-labelledby="quote-requirements-summary-title"
            className={`quote-requirements-summary${total ? '' : ' is-empty'}`}
            data-requirements-count={total}
            data-testid="quote-requirements-summary"
        >
            <h2 className="quote-requirements-summary-title" id="quote-requirements-summary-title">
                Quote requirements
            </h2>
            <div className="quote-requirements-summary-body">
                {total ? (
                    <ul className="quote-requirements-summary-list">
                        {items.map(item => (
                            <li
                                data-requirement-key={item.key}
                                data-testid="quote-requirements-summary-item"
                                key={item.key}
                            >
                                {item.label}
                                {item.detail ? (
                                    <span className="quote-requirements-summary-detail"> ({item.detail})</span>
                                ) : null}
                            </li>
                        ))}
                        {hiddenCount ? <li className="quote-requirements-summary-more">+{hiddenCount} more</li> : null}
                    </ul>
                ) : (
                    <span className="quote-requirements-summary-empty">None on this quote</span>
                )}
                {missingDefaultsCount ? (
                    <span className="quote-requirements-summary-note">
                        <InfoCircleOutlined aria-hidden /> {missingDefaultsCount} customer{' '}
                        {missingDefaultsCount === 1 ? 'default' : 'defaults'} not included
                    </span>
                ) : null}
            </div>
            <Button
                aria-label={total ? 'Edit quote requirements' : 'Add quote requirements'}
                className="quote-requirements-summary-action"
                data-testid="quote-requirements-summary-edit"
                onClick={onEdit}
                type="link"
            >
                {total ? 'Edit' : 'Add'}
            </Button>
        </section>
    );
});

export default QuoteRequirementsSummary;
